/**
 * MentalArmor — Mental Performance Training landing page.
 * Route: /mental-armor
 *
 * Shows the 6-week Friday-Lab elective structure, the 4-quarter master
 * curriculum, and today's daily "Mental Armor" prompt.
 * Attribution to Dr. Kristen Miller renders on every card.
 */
import React, { useEffect, useState } from 'react';
import { Brain, Shield, Compass, Users, Heart, Loader2 } from 'lucide-react';
import api from '../services/api';

const PILLAR_ICON = {
  Mindfulness: Brain,
  'Distress Tolerance': Shield,
  'Emotion Regulation': Heart,
  'Interpersonal Effectiveness': Users,
};

const PILLAR_COLOR = {
  Mindfulness: '#22D3EE',
  'Distress Tolerance': '#F59E0B',
  'Emotion Regulation': '#EC4899',
  'Interpersonal Effectiveness': '#A78BFA',
};

export default function MentalArmor() {
  const [daily, setDaily] = useState(null);
  const [curriculum, setCurriculum] = useState(null);
  const [elective, setElective] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [d, c, e] = await Promise.all([
          api.get('/academy/mental-armor/daily'),
          api.get('/academy/mental-armor/curriculum'),
          api.get('/academy/mental-armor/elective'),
        ]);
        setDaily(d.data);
        setCurriculum(c.data);
        setElective(e.data);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-cyan-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white py-12 px-4" data-testid="mental-armor-page">
      <div className="mx-auto max-w-5xl">

        {/* Hero */}
        <div className="mb-10">
          <div className="text-[10px] font-black tracking-[0.35em] text-cyan-400 inline-flex items-center gap-2">
            <Shield className="w-3.5 h-3.5" /> Mental Performance Training
          </div>
          <h1 className="mt-3 text-4xl sm:text-5xl font-black leading-tight">Mental Armor.</h1>
          <p className="mt-4 text-zinc-400 max-w-2xl">
            Building Champions, Body &amp; Mind — a full-season regulation curriculum for scholar-athletes.
            Five-minute daily prompts during the Enrichment Block. A 6-week elective in the Friday Leadership Lab.
            Skills that work on the field, at the dinner table, and in the classroom.
          </p>
          <p className="mt-4 text-[11px] italic text-cyan-300">
            {curriculum?.attribution}
          </p>
        </div>

        {/* Today's Daily Prompt */}
        {daily && (
          <div className="mb-10 rounded-2xl border-2 border-cyan-500/30 bg-gradient-to-br from-cyan-500/10 to-transparent p-6 sm:p-8" data-testid="daily-prompt-card">
            <div className="flex items-center justify-between">
              <div className="text-[10px] font-black tracking-[0.3em] text-cyan-400 inline-flex items-center gap-2">
                <Brain className="w-3.5 h-3.5" /> Today's Mental Armor
              </div>
              <div className="text-[10px] text-zinc-500 font-mono">{daily.date} · {daily.day}</div>
            </div>
            <h2 className="mt-3 text-2xl sm:text-3xl font-black">{daily.title}</h2>
            <div className="mt-2 text-xs text-zinc-500 uppercase tracking-widest">Tool: {daily.tool}</div>
            <p className="mt-5 text-lg text-zinc-200 leading-relaxed">{daily.prompt}</p>
            <p className="mt-5 text-[10px] italic text-zinc-500">{daily.attribution}</p>
          </div>
        )}

        {/* 6-Week Elective */}
        {elective && (
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-block w-2 h-6 bg-orange-500" />
              <h2 className="text-lg font-black uppercase tracking-widest">Friday Lab Elective · 6 Weeks</h2>
              <span className="text-xs text-zinc-500">{elective.block}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {elective.weeks.map((w) => (
                <div key={w.week} className="border border-zinc-800 p-5 bg-zinc-950" data-testid={`elective-week-${w.week}`}>
                  <div className="text-[10px] font-mono font-bold text-orange-400">WEEK {w.week}</div>
                  <div className="mt-1 text-lg font-black">{w.title}</div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {w.tools.map((t, i) => (
                      <span key={i} className="text-[10px] font-bold px-2 py-0.5 bg-cyan-500/10 text-cyan-300 border border-cyan-500/30">
                        {t}
                      </span>
                    ))}
                  </div>
                  <div className="mt-3 text-xs text-zinc-400"><strong className="text-zinc-200">Practice:</strong> {w.practice}</div>
                  <div className="mt-2 text-xs text-zinc-500 italic">Off-field bridge: {w.off_field_bridge}</div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-[10px] italic text-zinc-500">{elective.attribution}</p>
          </div>
        )}

        {/* Full-Year Curriculum by Quarter */}
        {curriculum && (
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-block w-2 h-6 bg-violet-500" />
              <h2 className="text-lg font-black uppercase tracking-widest">Full Year · 4 Quarters</h2>
              <span className="text-xs text-zinc-500">Aligned to sports seasons</span>
            </div>
            <div className="border border-zinc-800">
              {curriculum.quarters.map((q, i) => {
                const Icon = PILLAR_ICON[q.pillar] || Compass;
                const color = PILLAR_COLOR[q.pillar] || '#71717A';
                return (
                  <div key={q.quarter} className={`p-6 ${i > 0 ? 'border-t border-zinc-900' : ''}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}22`, border: `1px solid ${color}55` }}>
                        <Icon className="w-5 h-5" style={{ color }} />
                      </div>
                      <div className="flex-1">
                        <div className="text-[10px] font-mono font-bold" style={{ color }}>QUARTER {q.quarter} · {q.pillar.toUpperCase()}</div>
                        <div className="text-lg font-black text-white mt-0.5">{q.title}</div>
                      </div>
                    </div>
                    <p className="mt-3 text-sm text-zinc-300">{q.focus}</p>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {q.signature_tools.map((t, j) => (
                        <span key={j} className="text-[10px] font-bold px-2 py-0.5 bg-zinc-900 text-zinc-300 border border-zinc-800">
                          {t}
                        </span>
                      ))}
                    </div>
                    <div className="mt-3 text-xs text-zinc-500 italic">
                      <strong className="not-italic text-zinc-400">Quarter capstone:</strong> {q.assessment}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="mt-4 text-[10px] italic text-zinc-500">{curriculum.attribution}</p>
          </div>
        )}

      </div>
    </div>
  );
}
